// Raw-PCM helpers for the realtime voice mode. Flux STT wants linear16 mono
// at 16 kHz in; Aura TTS sends linear16 mono at 24 kHz back.

export const STT_SAMPLE_RATE = 16000;
export const TTS_SAMPLE_RATE = 24000;

// Deepgram browser websockets authenticate through subprotocols because the
// browser cannot set an Authorization header. Temp tokens (JWTs from
// /assistant/voice/token) use the "bearer" subprotocol.
export const buildDeepgramWsProtocols = (accessToken) => ["bearer", accessToken];

// AudioWorklet processor that downsamples the mic's Float32 frames to
// 16 kHz linear16 and posts ~50ms ArrayBuffers to the main thread. Shipped
// as a Blob URL so CRA needs no extra bundler config or public/ file.
const PCM_CAPTURE_PROCESSOR_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.targetSampleRate = ${STT_SAMPLE_RATE};
    this.chunkSamples = Math.round(this.targetSampleRate * 0.05);
    this.buffer = new Int16Array(this.chunkSamples);
    this.bufferOffset = 0;
    this.readPosition = 0;
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel || !channel.length) return true;

    const ratio = sampleRate / this.targetSampleRate;
    while (this.readPosition < channel.length) {
      const index = Math.floor(this.readPosition);
      const nextIndex = Math.min(index + 1, channel.length - 1);
      const fraction = this.readPosition - index;
      const sample =
        channel[index] * (1 - fraction) + channel[nextIndex] * fraction;
      const clamped = Math.max(-1, Math.min(1, sample));
      this.buffer[this.bufferOffset] =
        clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      this.bufferOffset += 1;
      this.readPosition += ratio;

      if (this.bufferOffset === this.chunkSamples) {
        const chunk = this.buffer.slice(0);
        this.port.postMessage(chunk.buffer, [chunk.buffer]);
        this.buffer = new Int16Array(this.chunkSamples);
        this.bufferOffset = 0;
      }
    }
    this.readPosition -= channel.length;
    return true;
  }
}
registerProcessor("pcm-capture-processor", PcmCaptureProcessor);
`;

export const createPcmCaptureWorkletUrl = () =>
  URL.createObjectURL(
    new Blob([PCM_CAPTURE_PROCESSOR_SOURCE], { type: "application/javascript" }),
  );

export const int16ToFloat32 = (arrayBuffer) => {
  const int16 = new Int16Array(arrayBuffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float32;
};
